<template>
  <v-dialog v-model="computedIsVisible" persistent max-width="756">
    <v-card>
      <v-card-title class="headline">{{ $i18n.t('dialog.header.massEditingOfAttributes') }}</v-card-title>
      <v-card-text>
        <template v-if="isFetched">
          <v-row no-gutters v-if="contentText">{{ contentText }}</v-row>
          <v-row no-gutters align="center" class="mt-5">
            <v-col cols="4" align-self="start">
              <v-select
                v-model="selectedAttribute"
                return-object
                :menu-props="{ offsetY: true }"
                outlined
                :label="$i18n.t('dashboard.filters.attribute')"
                :items="sortedFlatAttributes"
                item-value="name"
                item-text="label_pl"
                @change="setDefaultAttributeValue"
              >
                <template #selection="element">
                  <v-col cols="12" class="selected-attribute-selection">
                    {{ element.item[`label_${$i18n.locale}`] }}
                  </v-col>
                </template>
              </v-select>
            </v-col>
            <template v-if="selectedAttribute">
              <v-col class="px-5 pt-4" cols="auto" style="font-size: 30px" align-self="start">=</v-col>
              <v-col>
                <v-form ref="attributeValueForm" v-model="isAttributeValueValid">
                  <component
                    class="px-3"
                    v-if="fieldComponent(selectedAttribute)"
                    :is="fieldComponent(selectedAttribute)"
                    v-model="selectedAttribute.value"
                    :attribute="selectedAttribute"
                    :can-edit="true"
                    :is-label-visible="false"
                    :value-cols="12"
                  />
                  <template v-else>
                    <v-row no-gutters align="center">
                      <v-col v-if="selectedAttribute.type === 'boolean'" cols="12" class="px-0">
                        <v-select
                          v-model="selectedAttribute.value"
                          :readonly="false"
                          :items="dicts.boolean"
                          :item-text="$i18n.locale"
                          item-value="bool"
                          outlined
                        />
                      </v-col>
                      <KwNumberField
                        v-if="selectedAttribute.type === 'nr_kw' && entity !== 'offers'"
                        :attribute="selectedAttribute"
                        :can-edit="true"
                      />
                      <KwNumberFieldOffer
                        v-if="selectedAttribute.type === 'nr_kw' && entity === 'offers'"
                        :attribute.sync="selectedAttribute"
                        :can-edit="true"
                        :can-be-empty="false"
                      />
                      <SoilClass
                        v-if="selectedAttribute.type === 'kvp'"
                        :can-edit="true"
                        :is-label-visible="false"
                        v-model="selectedAttribute.value"
                      />
                      <PumField
                        v-if="selectedAttribute.type === 'pum'"
                        :can-edit="true"
                        :is-label-visible="false"
                        v-model="selectedAttribute.value"
                      />
                    </v-row>
                  </template>
                </v-form>
              </v-col>
            </template>
          </v-row>
        </template>
        <template v-else>
          <v-progress-circular color="primary" indeterminate size="25" />
        </template>
      </v-card-text>
      <v-card-actions>
        <div class="flex-grow-1" />
        <v-btn color="primary" text @click="close" :disabled="loading">{{
          $i18n.t('dialog.confirmation.cancel')
        }}</v-btn>
        <v-btn
          color="primary"
          text
          @click="openConfirmation"
          :disabled="!isAttributeValueValid || !selectedAttribute"
          :loading="loading"
          >{{ $i18n.t('dialog.confirmation.save') }}</v-btn
        >
      </v-card-actions>
    </v-card>
    <ConfirmDialog
      :dialog="isConfirmationVisible"
      @success="handleFieldCalculator"
      @cancel="isConfirmationVisible = false"
    >
      <template v-slot:header>
        <h1>{{ $i18n.t(`dialog.header.fieldCalculatorConfirmation`) }}</h1>
      </template>
      <template v-slot:content>
        <span>{{ $i18n.t('dialog.content.fieldCalculatorConfirmation') }}</span>
      </template>
    </ConfirmDialog>
  </v-dialog>
</template>
<script>
import { DICTS } from '@/dicts';
import { getFieldComponent } from '@/components/fields/fieldComponents';
export default {
  components: {
    KwNumberField: () => import('@/components/KwNumberField'),
    KwNumberFieldOffer: () => import('@/components/KwNumberFieldOffer'),
    SoilClass: () => import('@/components/SoilClass'),
    PumField: () => import('@/components/fields/PumField'),
    ConfirmDialog: () => import('@/components/ConfirmDialog')
  },
  props: {
    contentText: {
      type: String,
      default: ''
    },
    entity: {
      type: String,
      required: true
    },
    featuresIds: {
      type: Array,
      default: () => {
        return [];
      }
    },
    isVisible: {
      type: Boolean,
      default: () => {
        return false;
      }
    }
  },
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      }
    },
    defaultEmptyValues() {
      return {
        text: '',
        multiselect: [],
        select: null,
        nr_kw: this.entity === 'offers' ? ['//'] : '//',
        number: 0,
        boolean: false,
        price: 0,
        date_range: { from: 0, to: 0 },
        kvp: {}
      };
    },
    sortedFlatAttributes() {
      return JSON.parse(JSON.stringify(this.flatAttributes)).sort((a, b) => {
        return a[`label_${this.$i18n.locale}`] > b[`label_${this.$i18n.locale}`] ? 1 : -1;
      });
    }
  },
  data: () => ({
    flatAttributes: [],
    isAttributeValueValid: false,
    isConfirmationVisible: false,
    isFetched: false,
    loading: false,
    selectedAttribute: ''
  }),
  methods: {
    close() {
      this.selectedAttribute = '';
      this.computedIsVisible = false;
    },
    fieldComponent(attribute) {
      if (attribute.name === 'wojewodztwo') {
        return false;
      }
      if (attribute.name === 'powiat') {
        return false;
      }
      if (attribute.name === 'gmina') {
        return false;
      }
      return getFieldComponent(attribute);
    },
    openConfirmation() {
      this.isConfirmationVisible = true;
    },
    async getAttributesSchema() {
      this.isFetched = false;
      this.$store
        .dispatch('getAttributesSchema', { entity: this.entity, simpleAttributesOnly: true, publicSchema: false })
        .then(r => {
          if (r.status === 200) {
            const flatProperties = [];
            for (let group in r.data.data) {
              r.data.data[group].forEach(property => {
                flatProperties.push({
                  ...property,
                  value: this.defaultEmptyValues[property.type],
                  defaultValue: this.defaultEmptyValues[property.type],
                  required: true
                });
              });
            }
            this.flatAttributes = flatProperties.filter(property => property.type !== 'disabled');
          } else {
            this.$root.$emit('showSnackbar', this.$i18n.t(`default.error`));
            this.close();
          }
        });
      this.isFetched = true;
    },
    handleFieldCalculator() {
      this.isConfirmationVisible = false;
      this.loading = true;
      const payload = {
        entityType: this.entity.substring(0, this.entity.length - 1),
        data: {
          filtered_ids: this.featuresIds,
          calculator_expressions: [
            {
              attribute: this.selectedAttribute.name,
              value:
                this.selectedAttribute.name === 'kontakt_cbre' && this.entity === 'offers' // Brzydki tymczasowy fix
                  ? this.selectedAttribute.value.join(',')
                  : this.selectedAttribute.value
            }
          ]
        }
      };
      this.$store.dispatch('fieldCalculator', payload).then(r => {
        if (r.status === 200 || r.status === 204) {
          this.$root.$emit('showSnackbar', this.$i18n.t('offer.offers.edit200'));
          this.$emit('success');
          this.loading = false;
          this.close();
        } else {
          this.$root.$emit('showSnackbar', this.$i18n.t('offer.offers.errorDB'));
          this.loading = false;
        }
      });
    },
    setDefaultAttributeValue(nV) {
      this.selectedAttribute.value = JSON.parse(JSON.stringify(nV.defaultValue));
    }
  },
  watch: {
    entity() {
      this.getAttributesSchema();
    },
    selectedAttribute() {
      this.$nextTick(() => {
        if (this.$refs.attributeValueForm) {
          this.$refs.attributeValueForm.resetValidation();
        }
      });
    }
  },
  mounted() {
    this.dicts = DICTS;
    this.getAttributesSchema();
  }
};
</script>
<style lang="scss" scoped>
.selected-attribute-selection {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>